import { isEqual, isUndefined } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { fetchAllEntityJurisdictionAnalytics, fetchDocumentSpreadAnalytics, fetchEntitySpreadAnalytics, fetchHomeEntitySpreadAnalytics, fetchMyAttestationProgressAnalytics } from '../../../services/analytics';
import { enquireModule } from '../../../services/auth';
import { updateCustomFilters } from '../../../services/users';
import { updateUserCustomFilters } from '../../auth/login/store/actions';
import { getHomeScreenModules, getUser } from '../../auth/login/store/selectors';
import { CustomFilters, HomePageModule } from '../../auth/login/store/types';
import { fetchAllDocumentsRiskToleranceStarted } from '../../documents/analytics/store';
import { PieChartAnalyticsData } from '../../shared/analytics/PieChart';
import {
    enquireModuleFailed,
    enquireModuleSuccessful,
    fetchHomepageAttestationProgressFailed,
    fetchHomepageAttestationProgressSuccessful,
    fetchHomepageDocumentSpreadFailed,
    fetchHomepageDocumentSpreadStarted,
    fetchHomepageDocumentSpreadSuccessful,
    fetchHomepageEntityJurisdictionFailed,
    fetchHomepageEntityJurisdictionStarted,
    fetchHomepageEntityJurisdictionSuccessful,
    fetchHomepageEntitySpreadFailed,
    fetchHomepageEntitySpreadStarted,
    fetchHomepageEntitySpreadSuccessful,
    fetchHomepageHomeEntitySpreadFailed,
    fetchHomepageHomeEntitySpreadSuccessful,
    fetchRelevantHomeScreenAnalytics,
    redirectMyAttestation,
    setCustomFiltersUpdated,
    toggleUnauthorisedModuleModalOpen,
    updateCustomFiltersFailed,
    updateCustomFiltersSuccessful
} from './actions';
import { getCurrentUserCustomFilters, getIsSavingCustomFilters, getSavedUserCustomFilters, getUnauthorisedModule } from './selectors';
import { ArkModule, EntityJurisdictionAnalytics, HomepageAnalyticsActionTypes, MyAttestationAnalytics } from './types';
import { fetchOpinionsAverageStarted } from '../../opinions/analytics/store/actions';
import { fetchPageUsageAnalyticsStarted } from '../../admin/analytics/store/actions';
import { fetchAllUsersStarted } from '../../admin/users/store/actions';
import { fetchAllRolesStarted } from '../../admin/roles/store/actions';
import { fetchAllOpinionsByJurisdictionStarted } from '../../opinions/my-opinions/store/actions';

export function* attemptFetchHomepageDocumentSpread() {
    try {
        const analytics: PieChartAnalyticsData[] = yield call(fetchDocumentSpreadAnalytics, { entityId: null });
        yield put(fetchHomepageDocumentSpreadSuccessful(analytics));
    } catch (e) {
        yield put(fetchHomepageDocumentSpreadFailed((e as Error).message));
    }
}

function* fetchHomepageDocumentSpreadWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_HOMEPAGE_DOCUMENT_SPREAD_ANALYTICS_STARTED, attemptFetchHomepageDocumentSpread);
}

export function* attemptFetchHomepageEntitySpread() {
    try {
        const analytics: PieChartAnalyticsData[] = yield call(fetchEntitySpreadAnalytics, { entityId: null });
        yield put(fetchHomepageEntitySpreadSuccessful(analytics));
    } catch (e) {
        yield put(fetchHomepageEntitySpreadFailed((e as Error).message));
    }
}

function* fetchHomepageEntitySpreadWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_HOMEPAGE_ENTITY_SPREAD_ANALYTICS_STARTED, attemptFetchHomepageEntitySpread);
}

export function* attemptFetchHomepageHomeEntitySpread() {
    try {
        const analytics: PieChartAnalyticsData[] = yield call(fetchHomeEntitySpreadAnalytics, { entityId: null });
        yield put(fetchHomepageHomeEntitySpreadSuccessful(analytics));
    } catch (e) {
        yield put(fetchHomepageHomeEntitySpreadFailed((e as Error).message));
    }
}

function* fetchHomepageHomeEntitySpreadWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_HOMEPAGE_HOME_ENTITY_SPREAD_ANALYTICS_STARTED, attemptFetchHomepageHomeEntitySpread);
}

export function* attemptFetchMyAttestationProgress() {
    try {
        const analytics: MyAttestationAnalytics[] = yield call(fetchMyAttestationProgressAnalytics);
        yield put(fetchHomepageAttestationProgressSuccessful(analytics));
    } catch (e) {
        yield put(fetchHomepageAttestationProgressFailed((e as Error).message));
    }
}

function* fetchMyAttestationProgressWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_HOMEPAGE_ATTESTATION_PROGRESS_ANALYTICS_STARTED, attemptFetchMyAttestationProgress);
}

export function* attemptFetchCounterpartyJurisdiction() {
    try {
        const analytics: EntityJurisdictionAnalytics[] = yield call(fetchAllEntityJurisdictionAnalytics);
        yield put(fetchHomepageEntityJurisdictionSuccessful(analytics));
    } catch (e) {
        yield put(fetchHomepageEntityJurisdictionFailed((e as Error).message));
    }
}

function* fetchCounterpartyJurisdictionWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_HOMEPAGE_ENTITY_JURISDICTION_ANALYTICS_STARTED, attemptFetchCounterpartyJurisdiction);
}

export function* redirectMyAttestationAnalytics({ payload }: ReturnType<typeof redirectMyAttestation>) {
    const navigationPath = `/my-attestations/${payload}`;
    yield put(push(navigationPath));
}

function* redirectMyAttestationWatcher() {
    yield takeLatest(HomepageAnalyticsActionTypes.REDIRECT_ANALYTICS_MY_ATTESTATION, redirectMyAttestationAnalytics);
}

export function* openUnauthorisedModuleModal() {
    yield put(toggleUnauthorisedModuleModalOpen(true));
}

function* unauthorisedModuleWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.SET_UNAUTHORISED_MODULE, openUnauthorisedModuleModal);
}

export function* attemptEnquireModule() {
    try {
        const module: ArkModule = yield select(getUnauthorisedModule);
        yield call(enquireModule, { module });
        yield put(enquireModuleSuccessful());
    } catch (e) {
        yield put(enquireModuleFailed((e as Error).message));
    }
}

function* enquireModuleWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.ENQUIRE_MODULE_STARTED, attemptEnquireModule);
}

export function* attemptUpdateCustomFilters() {
    try {
        const currentUserCustomFilters: CustomFilters[] = yield select(getCurrentUserCustomFilters);
        yield call(updateCustomFilters, { customFilters: currentUserCustomFilters });
        yield put(updateCustomFiltersSuccessful(currentUserCustomFilters));
        yield put(updateUserCustomFilters(currentUserCustomFilters));
        yield put(setCustomFiltersUpdated(false));
        toast('Custom filters updated');
    } catch (e) {
        yield put(updateCustomFiltersFailed((e as Error).message));
        toast.error('Unable to update custom filters. Please try again.');
    }
}

function* attemptUpdateCustomFiltersWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.UPDATE_CUSTOM_FILTERS_STARTED, attemptUpdateCustomFilters);
}

export function* checkCustomFiltersUpdated() {
    const isSaving: boolean = yield select(getIsSavingCustomFilters);
    const currentUserCustomFilters: CustomFilters[] = yield select(getCurrentUserCustomFilters);
    const savedUserCustomFilters: CustomFilters[] = yield select(getSavedUserCustomFilters);
    if (!isSaving) {
        const updated = !isEqual(currentUserCustomFilters, savedUserCustomFilters);
        yield put(setCustomFiltersUpdated(updated));
    }
}

function* checkCustomFiltersUpdatedWatcher() {
    yield debounce(500, [HomepageAnalyticsActionTypes.UPDATE_CUSTOM_FILTER_VALUES, HomepageAnalyticsActionTypes.ADD_OR_DELETE_USER_CUSTOM_FILTER], checkCustomFiltersUpdated);
}

const potentialAnalyticsToFetch = [
    { id: HomePageModule.DOCUMENTS, actions: [fetchHomepageDocumentSpreadStarted, fetchHomepageEntitySpreadStarted, fetchAllDocumentsRiskToleranceStarted, fetchHomepageEntityJurisdictionStarted] },
    { id: HomePageModule.OPINIONS, actions: [fetchOpinionsAverageStarted, fetchAllOpinionsByJurisdictionStarted] },
    { id: HomePageModule.ADMIN, actions: [fetchPageUsageAnalyticsStarted, fetchAllUsersStarted, fetchAllRolesStarted] }
];

export function* attemptFetchRelevantHomeScreenAnalytics({ payload }: ReturnType<typeof fetchRelevantHomeScreenAnalytics>) {
    let user = payload;
    if (isUndefined(user)) {
        user = yield select(getUser);
    }
    const modules: HomePageModule[] = yield select(getHomeScreenModules);
    for (const module of modules) {
        const analyticsToFetch = potentialAnalyticsToFetch.find(({ id }) => id === module);
        if (analyticsToFetch && analyticsToFetch.actions.length > 0) {
            for (const action of analyticsToFetch.actions) {
                yield put(action());
            }
        }
    }
}

function* relevantHomeScreenAnalyticsWatcher() {
    yield takeLeading(HomepageAnalyticsActionTypes.FETCH_RELEVANT_HOME_SCREEN_ANALYTICS, attemptFetchRelevantHomeScreenAnalytics);
}

export function* homepageSaga() {
    yield all([
        fork(fetchHomepageDocumentSpreadWatcher),
        fork(fetchHomepageEntitySpreadWatcher),
        fork(fetchHomepageHomeEntitySpreadWatcher),
        fork(fetchMyAttestationProgressWatcher),
        fork(redirectMyAttestationWatcher),
        fork(enquireModuleWatcher),
        fork(unauthorisedModuleWatcher),
        fork(relevantHomeScreenAnalyticsWatcher),
        fork(attemptUpdateCustomFiltersWatcher),
        fork(checkCustomFiltersUpdatedWatcher),
        fork(fetchCounterpartyJurisdictionWatcher)
    ]);
}
